import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { merchantChatApi } from './merchant-chat-api';

/** The old drawer's page sizes, verbatim. */
export const MERCHANT_CHATS_PAGE_SIZE = 10;
export const MERCHANT_THREAD_PAGE_SIZE = 15;

/** Query-key factory for the merchant-chat slice. */
export const merchantChatKeys = {
  all: ['merchant-chats'] as const,
  lists: () => [...merchantChatKeys.all, 'list'] as const,
  list: (username: string) => [...merchantChatKeys.lists(), username] as const,
  threads: () => [...merchantChatKeys.all, 'thread'] as const,
  thread: (chatId: number, page: number) =>
    [...merchantChatKeys.threads(), chatId, page] as const,
};

export const merchantChatQueries = {
  /**
   * The conversation list, newest first. Infinite (skip/limit pages) — the
   * drawer's scroll-up appends the next page the way the old drawer merged it
   * into its Redux array.
   */
  list: (username: string) =>
    infiniteQueryOptions({
      queryKey: merchantChatKeys.list(username),
      initialPageParam: 0,
      queryFn: ({ pageParam }) =>
        merchantChatApi.list({
          skip: pageParam * MERCHANT_CHATS_PAGE_SIZE,
          limit: MERCHANT_CHATS_PAGE_SIZE,
          ...(username ? { username } : {}),
        }),
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((count, page) => count + page.data.length, 0);
        return loaded < lastPage.count ? pages.length : undefined;
      },
    }),

  /**
   * One thread, cumulative: page N re-reads the newest `15 × N` (skip 0),
   * exactly like the old window's `limit: 15 * pageCount`. Growing the page
   * keeps the previous messages visible while the longer slice loads.
   */
  thread: (chatId: number, page: number) =>
    queryOptions({
      queryKey: merchantChatKeys.thread(chatId, page),
      queryFn: () =>
        merchantChatApi.thread(chatId, {
          skip: 0,
          limit: MERCHANT_THREAD_PAGE_SIZE * page,
        }),
      placeholderData: (previous) => previous,
    }),
};
